import { CheckCircle2, CircleHelp, GitCompare, Loader, OctagonAlert } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button, DiagnosticCopy } from '@krp/ui-kit'

import type { CommandView } from '../model/commandView'
import { metaOf } from './meta'

/**
 * Итог пишущей команды. Каждый исход — свой значок, рамка и слова, а не цвет
 * (экран в `grayscale(1)` читаем, CLAUDE.md §7). «Неизвестно» не повторяется
 * само: человек решает, проверить ли исход той же командой.
 */
const ICONS: Record<CommandView['status'], ReactNode> = {
  sending: <Loader size={18} aria-hidden="true" />,
  succeeded: <CheckCircle2 size={18} aria-hidden="true" />,
  unknown: <CircleHelp size={18} aria-hidden="true" />,
  conflict: <GitCompare size={18} aria-hidden="true" />,
  failed: <OctagonAlert size={18} aria-hidden="true" />,
}

const TITLES: Record<CommandView['status'], string> = {
  sending: 'Отправляем…',
  succeeded: 'Готово',
  unknown: 'Исход неизвестен',
  conflict: 'Данные в 1С изменились',
  failed: 'Не выполнено',
}

/** Текст для передачи администратору: что делалось, чем кончилось, дословный ответ, код, статус и запрос. */
function reportOf(command: CommandView): string {
  const error = command.error
  const lines = [`${command.label}: ${TITLES[command.status]}`]
  if (command.message !== null) lines.push(command.message)
  if (error !== null) {
    lines.push(`Код: ${error.code}`)
    if (error.status !== undefined) lines.push(`HTTP: ${error.status}`)
    if (error.correlationId !== null) lines.push(`Запрос: ${error.correlationId}`)
  }
  return lines.join('\n')
}

export function CommandStatus({
  command,
  onResend,
  onDismiss,
  children,
}: {
  command: CommandView
  onResend?: () => void
  onDismiss?: () => void
  children?: ReactNode
}) {
  const urgent = command.status === 'failed' || command.status === 'unknown' || command.status === 'conflict'
  return (
    <div className="onec-command" data-status={command.status} role={urgent ? 'alert' : 'status'}>
      {ICONS[command.status]}
      <div className="onec-command__body">
        <p className="onec-command__title">
          {command.label}: {TITLES[command.status]}
        </p>
        {command.message !== null && <p className="onec-command__text">{command.message}</p>}
        {command.status === 'unknown' && (
          <p className="onec-command__text">
            Ответ не получен: неизвестно, выполнена ли операция в 1С. Проверка отправит ту же команду с тем же ключом —
            служба вернёт прежний итог и второй записи не сделает.
          </p>
        )}
        {command.error !== null && (
          <p className="onec-command__meta">
            {metaOf(command.error.code, command.error.correlationId)}
            {command.error.status !== undefined && <span> · HTTP {command.error.status}</span>}
          </p>
        )}
        {command.error !== null && <DiagnosticCopy text={reportOf(command)} />}
        <div className="onec-command__actions">
          {command.resendable && onResend !== undefined && (
            <Button variant="primary" onClick={onResend}>
              {command.status === 'unknown' ? 'Проверить исход' : 'Отправить ещё раз'}
            </Button>
          )}
          {children}
          {command.status !== 'sending' && command.status !== 'unknown' && onDismiss !== undefined && (
            <Button variant="ghost" onClick={onDismiss}>
              Скрыть
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
